import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { instructors as instructorsApi } from '@/api/endpoints'
import { Checkbox, Field, Input, Spinner, Textarea } from '@/components/ui'
import { ChipGroup, courseChips, languageChips } from '@/components/ui/ChipGroup'
import { useBootstrap } from '@/hooks/useBootstrap'
import { parseIds } from '@/lib/utils'
import type { Instructor } from '@/types/api'

type Editable = Pick<Instructor, 'fname' | 'sname' | 'email' | 'tel' | 'note' | 'favorite'> & {
  language_ids: number[]
  course_ids: number[]
}

/**
 * Údaje inštruktora - ten istý formulár pre úpravu aj pre nového.
 *
 * Bez `instructor` zakladá, s ním upravuje. Jazyky a kurzy sú pri zakladaní
 * prázdne zámerne: sú to schopnosti konkrétneho človeka, nie predvoľba, a
 * kalendár podľa nich ponúka, koho možno k lekcii priradiť.
 */
export default function InstructorForm({
  instructor,
  onDone,
}: {
  instructor?: Instructor
  onDone: (instructor: Instructor) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data: codebooks } = useBootstrap()
  // Len jazyky, ktoré škola učí - inštruktora nemá zmysel označiť jazykom,
  // v ktorom sa lekcia nedá objednať.
  const active = (codebooks?.languages ?? []).filter((row) => row.use !== false)

  const [form, setForm] = useState<Editable>(() => ({
    fname: instructor?.fname ?? '',
    sname: instructor?.sname ?? '',
    email: instructor?.email ?? '',
    tel: instructor?.tel ?? '',
    note: instructor?.note ?? '',
    favorite: instructor?.favorite ?? false,
    // Zoznamy id sa v databáze držia ako `"1,2,3"`; API ich prijíma ako pole.
    language_ids: parseIds(instructor?.idslang),
    course_ids: parseIds(instructor?.idscourses),
  }))

  const set = <K extends keyof Editable>(key: K, value: Editable[K]) =>
    setForm((current) => ({ ...current, [key]: value }))


  const save = useMutation({
    mutationFn: () =>
      instructor ? instructorsApi.update(instructor.id, form) : instructorsApi.create(form),
    onSuccess: async (saved) => {
      qc.setQueryData(['instructor', saved.id], saved)
      await qc.invalidateQueries({ queryKey: ['instructors'] })
      // Kalendár ukazuje mená a jazyky inštruktorov, takže ho treba obnoviť tiež.
      void qc.invalidateQueries({ queryKey: ['calendar'] })
      onDone(saved)
    },
  })

  const fieldError = (name: keyof Editable) => {
    const data = (save.error as { response?: { data?: Record<string, unknown> } })?.response?.data
    const value = data?.[name]
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
    return typeof value === 'string' ? value : undefined
  }

  return (
    <form
      className="space-y-3 rounded-md border border-slate-200 p-3"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('reservation.firstname')} error={fieldError('fname')}>
          <Input value={form.fname ?? ''} onChange={(e) => set('fname', e.target.value)} />
        </Field>
        <Field label={t('reservation.surname')} error={fieldError('sname')}>
          <Input value={form.sname ?? ''} onChange={(e) => set('sname', e.target.value)} />
        </Field>
        <Field label={t('reservation.phone')} error={fieldError('tel')}>
          <Input value={form.tel ?? ''} onChange={(e) => set('tel', e.target.value)} />
        </Field>
        <Field label={t('reservation.email')} error={fieldError('email')}>
          <Input
            type="email"
            value={form.email ?? ''}
            onChange={(e) => set('email', e.target.value)}
          />
        </Field>
      </div>

      {/* Ako v rezervácii inštruktora - tlačidlá s kľúčom jazyka a kurzu. */}
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label={t('instructors.languages')} error={fieldError('language_ids')}>
          <ChipGroup
            options={languageChips(active)}
            value={form.language_ids}
            onChange={(next) => set('language_ids', next)}
          />
        </Field>

        <Field label={t('instructors.courses')} error={fieldError('course_ids')}>
          <ChipGroup
            options={courseChips(codebooks?.courses ?? [])}
            value={form.course_ids}
            onChange={(next) => set('course_ids', next)}
          />
        </Field>
      </div>

      <Field label={t('global.note')} error={fieldError('note')}>
        <Textarea value={form.note ?? ''} onChange={(e) => set('note', e.target.value)} />
      </Field>

      <Checkbox
        label={t('instructors.favorite')}
        checked={form.favorite}
        onChange={(e) => set('favorite', e.target.checked)}
      />

      {save.isError ? (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {t('clients.saveFailed')}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <button type="submit" className="btn-primary" disabled={save.isPending}>
          {save.isPending ? <Spinner /> : null}
          {instructor ? t('global.save') : t('instructors.create')}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => onDone(instructor as Instructor)}
        >
          {t('global.cancel')}
        </button>
      </div>
    </form>
  )
}
